import { useState, useEffect } from 'react';
import { ShoppingBag, TrendingUp, Layers, Users, Loader2 } from 'lucide-react';
import { platformFetch } from '../../../platform/http';

interface EcommerceStats {
  totalTasks: number;
  todayTasks: number;
  totalNodes: number;
  statusDistribution: Array<{ status: string; count: number }>;
  modelDistribution: Array<{ model: string; count: number }>;
  topUsers: Array<{ userId: string; email: string; name: string; taskCount: number }>;
  dailyTrend: Array<{ date: string; count: number }>;
}

const statusMap: Record<string, { label: string; color: string }> = {
  idle: { label: '待生成', color: 'bg-gray-400' },
  generating: { label: '生成中', color: 'bg-blue-500' },
  success: { label: '成功', color: 'bg-green-500' },
  failed: { label: '失败', color: 'bg-red-500' },
};

export default function EcommerceStatsTab() {
  const [stats, setStats] = useState<EcommerceStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // platformFetch 统一鉴权（含代登录会话）与 API_BASE；此前直读 localStorage
        // 在代登录下拿错 token，且硬编码 /api 前缀在非默认 API_BASE 环境打错地址
        const res = await platformFetch('/admin/ecommerce-replicate/stats');
        const data = await res.json();
        setStats(data);
      } catch (err) {
        console.error('获取复刻统计失败:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" /></div>;
  }
  if (!stats) return <p className="text-center py-12 text-light-muted">加载失败</p>;

  const maxDaily = Math.max(...stats.dailyTrend.map((d) => d.count), 1);
  const successRate = stats.statusDistribution.length > 0
    ? (() => {
        const success = stats.statusDistribution.find((s) => s.status === 'success')?.count || 0;
        const failed = stats.statusDistribution.find((s) => s.status === 'failed')?.count || 0;
        const finished = success + failed;
        return finished > 0 ? Math.round((success / finished) * 100) : 0;
      })()
    : 0;

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* 统计卡片 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-light-muted text-sm">任务总数</p>
              <p className="text-2xl lg:text-3xl font-bold text-light-text mt-2">{stats.totalTasks.toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-blue-500 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-light-muted text-sm">今日新建</p>
              <p className="text-2xl lg:text-3xl font-bold text-green-600 mt-2">{stats.todayTasks}</p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-green-500 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-light-muted text-sm">节点总数</p>
              <p className="text-2xl lg:text-3xl font-bold text-accent-blue mt-2">{stats.totalNodes.toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-purple-500 flex items-center justify-center">
              <Layers className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-light-muted text-sm">生成成功率</p>
              <p className="text-2xl lg:text-3xl font-bold text-orange-500 mt-2">{successRate}%</p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-orange-500 flex items-center justify-center">
              <span className="text-white text-lg font-bold">%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* 节点状态分布 */}
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <h3 className="text-sm font-medium text-light-muted mb-4">节点状态分布</h3>
          <div className="space-y-3">
            {stats.statusDistribution.map((item) => {
              const pct = stats.totalNodes > 0 ? Math.round((item.count / stats.totalNodes) * 100) : 0;
              const info = statusMap[item.status] || { label: item.status, color: 'bg-gray-300' };
              return (
                <div key={item.status}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-light-text">{info.label}</span>
                    <span className="text-light-muted">{item.count} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-light-hover rounded-full h-2">
                    <div className={`${info.color} rounded-full h-2 transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {stats.statusDistribution.length === 0 && <p className="text-sm text-light-muted text-center py-4">暂无数据</p>}
          </div>
        </div>

        {/* 模型使用分布 */}
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <h3 className="text-sm font-medium text-light-muted mb-4">模型使用分布</h3>
          <div className="space-y-3">
            {stats.modelDistribution.map((item) => {
              const pct = stats.totalNodes > 0 ? Math.round((item.count / stats.totalNodes) * 100) : 0;
              return (
                <div key={item.model}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-light-text">{item.model || '未知'}</span>
                    <span className="text-light-muted">{item.count} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-light-hover rounded-full h-2">
                    <div className="bg-accent-blue rounded-full h-2 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {stats.modelDistribution.length === 0 && <p className="text-sm text-light-muted text-center py-4">暂无数据</p>}
          </div>
        </div>
      </div>

      {/* 7 天趋势 + 活跃用户 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <h3 className="text-sm font-medium text-light-muted mb-4">近 7 天新建任务</h3>
          <div className="flex items-end gap-2 h-40">
            {stats.dailyTrend.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-light-muted">{d.count}</span>
                <div className="w-full bg-accent-blue/20 rounded-t relative" style={{ height: `${Math.max((d.count / maxDaily) * 100, 4)}%` }}>
                  <div className="absolute inset-x-0 bottom-0 bg-accent-blue rounded-t" style={{ height: '100%' }} />
                </div>
                <span className="text-xs text-light-muted">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-light-muted" />
            <h3 className="text-sm font-medium text-light-muted">活跃用户 TOP10</h3>
          </div>
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-light-border">
                  <th className="text-left text-xs font-medium text-light-muted py-2 px-3">排名</th>
                  <th className="text-left text-xs font-medium text-light-muted py-2 px-3">用户邮箱</th>
                  <th className="text-right text-xs font-medium text-light-muted py-2 px-3">任务数</th>
                </tr>
              </thead>
              <tbody>
                {stats.topUsers.map((u, i) => (
                  <tr key={u.userId} className="border-b border-light-border last:border-0">
                    <td className="py-2 px-3 text-sm text-light-muted">{i + 1}</td>
                    <td className="py-2 px-3 text-sm text-light-text">{u.email}</td>
                    <td className="py-2 px-3 text-sm text-right font-medium text-accent-blue">{u.taskCount}</td>
                  </tr>
                ))}
                {stats.topUsers.length === 0 && (
                  <tr><td colSpan={3} className="text-center py-4 text-sm text-light-muted">暂无数据</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-light-border lg:hidden">
            {stats.topUsers.map((u, i) => (
              <div key={u.userId} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-light-hover px-2 text-xs font-medium text-light-text">
                        #{i + 1}
                      </span>
                      <p className="truncate text-sm font-medium text-light-text">{u.email}</p>
                    </div>
                    <p className="mt-1 text-xs text-light-muted">{u.name || '未设置昵称'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-light-muted">任务数</p>
                    <p className="text-sm font-semibold text-accent-blue">{u.taskCount}</p>
                  </div>
                </div>
              </div>
            ))}
            {stats.topUsers.length === 0 && (
              <div className="py-4 text-center text-sm text-light-muted">暂无数据</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
